/**
 * Lógica pura compartida del editor WYSIWYG (formatos, URL de enlaces).
 * El hook `useWysiwyg` orquesta Selection API y estado; aquí vive el razonamiento reutilizable y testeable.
 */

/** Comandos tipo `document.execCommand` → tag HTML para alternar formato. */
export const WYSIWYG_COMMAND_TO_TAG: Record<string, string> = {
  bold: 'strong',
  italic: 'em',
  underline: 'u',
  strikeThrough: 's',
};

/**
 * Normaliza texto de URL para atributo `href` (trim, `https://` por defecto).
 * @returns `null` si el input queda vacío.
 */
export function normalizeLinkHref(input: string): string | null {
  let validUrl = input.trim();
  if (!validUrl) return null;
  if (!validUrl.match(/^https?:\/\//i)) {
    validUrl = 'https://' + validUrl;
  }
  return validUrl;
}

function addFormatsFromElement(element: HTMLElement, formats: Set<string>): void {
  const tagName = element.tagName.toLowerCase();
  const style = window.getComputedStyle(element);

  if (tagName === 'strong' || tagName === 'b') formats.add('bold');
  if (tagName === 'em' || tagName === 'i') formats.add('italic');
  if (tagName === 'u') formats.add('underline');
  if (tagName === 's' || tagName === 'strike' || tagName === 'del') formats.add('strikethrough');
  if (tagName === 'ul') formats.add('ul');
  if (tagName === 'ol') formats.add('ol');

  if (parseInt(style.fontWeight) >= 700) formats.add('bold');
  if (style.fontStyle === 'italic') formats.add('italic');
  if (style.textDecoration.includes('underline')) formats.add('underline');
  if (style.textDecoration.includes('line-through')) formats.add('strikethrough');
}

function elementMatchesFormat(tag: string, element: HTMLElement): boolean {
  const tagName = element.tagName.toLowerCase();

  if (tag === 'bold' && (tagName === 'strong' || tagName === 'b')) return true;
  if (tag === 'italic' && (tagName === 'em' || tagName === 'i')) return true;
  if (tag === 'underline' && tagName === 'u') return true;
  if (tag === 'strikethrough' && (tagName === 's' || tagName === 'strike' || tagName === 'del'))
    return true;
  if (tag === 'ul' && tagName === 'ul') return true;
  if (tag === 'ol' && tagName === 'ol') return true;

  const style = window.getComputedStyle(element);
  if (tag === 'bold' && parseInt(style.fontWeight) >= 700) return true;
  if (tag === 'italic' && style.fontStyle === 'italic') return true;
  if (tag === 'underline' && style.textDecoration.includes('underline')) return true;
  if (tag === 'strikethrough' && style.textDecoration.includes('line-through')) return true;

  return false;
}

/**
 * Acumula formatos activos recorriendo ancestros desde el nodo común del rango hasta `editorRoot` (excl.).
 * @returns `null` si la selección no debe actualizar formatos (fuera del editor cuando hay root).
 */
export function collectActiveFormatsFromAncestors(
  range: Range,
  editorRoot: HTMLElement | null
): Set<string> | null {
  if (editorRoot && !editorRoot.contains(range.commonAncestorContainer)) {
    return null;
  }

  const formats = new Set<string>();
  let node: Node | null = range.commonAncestorContainer;

  while (node && node !== editorRoot) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      addFormatsFromElement(node as HTMLElement, formats);
    }
    node = node.parentNode;
  }

  return formats;
}

/**
 * Indica si `tag` (nombre lógico: bold, italic, …) está activo en la selección actual.
 */
export function isFormatActiveAtRange(
  tag: string,
  range: Range,
  editorRoot: HTMLElement | null
): boolean {
  let node: Node | null = range.commonAncestorContainer;

  while (node && node !== editorRoot) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (elementMatchesFormat(tag, node as HTMLElement)) return true;
    }
    node = node.parentNode;
  }

  return false;
}
